import { syncSearchFieldStateFromUrl } from './search-form-clear.js';

function debounce(fn, delay) {
	let timeoutId = null;

	return function debounced(...args) {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		timeoutId = setTimeout(function () {
			fn.apply(this, args);
		}, delay);
	};
}

function buildPageUrl(form) {
	const url = new URL(window.location.href);
	const formData = new FormData(form);
	const hasSearchInput = Boolean(form.querySelector('input[name="s"]'));

	url.search = '';

	Array.from(formData.entries()).forEach(function ([key, value]) {
		const normalizedValue = typeof value === 'string' ? value.trim() : value;

		if (key === 's' && hasSearchInput) {
			url.searchParams.set('s', normalizedValue);
			return;
		}

		if (!normalizedValue) {
			return;
		}

		if ((key === 'page' || key === 'paged') && String(normalizedValue) === '1') {
			return;
		}

		url.searchParams.set(key, normalizedValue);
	});

	if (hasSearchInput && !url.searchParams.has('s')) {
		url.searchParams.set('s', '');
	}

	return url;
}

/**
 * Sync the search page hero heading with the committed query in the URL.
 *
 * @param {string} searchQuery Trimmed search query from the request URL.
 */
function updateSearchPageHeading(searchQuery) {
	const heading = document.querySelector('.js-search-page-heading');

	if (!heading) {
		return;
	}

	const landingText = heading.dataset.searchHeadingLanding || '';
	const resultsTemplate = heading.dataset.searchHeadingResults || '';

	if (!searchQuery) {
		heading.textContent = landingText;
		return;
	}

	heading.textContent = resultsTemplate.replace('%s', searchQuery);
}

/**
 * Show or hide popular searches based on landing vs active search state.
 *
 * @param {string} searchQuery Trimmed search query from the request URL.
 */
function updateSearchPagePopularVisibility(searchQuery) {
	const popular = document.querySelector('.js-search-page-popular');

	if (!popular) {
		return;
	}

	popular.hidden = Boolean(searchQuery);
}

function setLoadingState(form, results, isLoading) {
	const stringValue = isLoading ? 'true' : 'false';

	form.dataset.loading = stringValue;
	results.dataset.loading = stringValue;
	results.setAttribute('aria-busy', stringValue);
}

function updatePageField(form, pageNumber) {
	const pageField = form.querySelector('input[name="page"], input[name="paged"]');

	if (!pageField) {
		return;
	}

	pageField.value = String(pageNumber);
}

function initAsyncForm(form) {
	const endpoint = form.dataset.gmrEndpoint || '';
	const resultsSelector = form.dataset.gmrResults || '';
	const results = resultsSelector ? document.querySelector(resultsSelector) : null;
	const shouldScrollPageChangesToResults = form.dataset.gmrPageScroll === 'results';

	if (!endpoint || !results) {
		return;
	}

	let controller = null;

	const requestResults = function (options = {}) {
		const requestUrl = buildPageUrl(form);
		const endpointUrl = new URL(endpoint, window.location.origin);
		const savedScrollY = options.preserveScroll ? window.scrollY : null;

		endpointUrl.search = requestUrl.search;

		if (controller) {
			controller.abort();
		}

		controller =
			typeof window.AbortController === 'function'
				? new window.AbortController()
				: null;

		setLoadingState(form, results, true);

		fetch(endpointUrl.toString(), {
			credentials: 'same-origin',
			headers: {
				Accept: 'application/json',
			},
			signal: controller ? controller.signal : undefined,
		})
			.then(function (response) {
				if (!response.ok) {
					throw new Error('Async content request failed.');
				}

				return response.json();
			})
			.then(function (data) {
				if (!data || typeof data.html !== 'string') {
					throw new Error('Async content response is invalid.');
				}

				const searchQuery = requestUrl.searchParams.get('s')?.trim() || '';

				results.innerHTML = data.html;
				window.history.replaceState({}, '', requestUrl);
				updateSearchPageHeading(searchQuery);
				updateSearchPagePopularVisibility(searchQuery);
				syncSearchFieldStateFromUrl(Boolean(searchQuery));

				if (savedScrollY !== null) {
					window.scrollTo(0, savedScrollY);
				}

				if (options.focusResults) {
					results.setAttribute('tabindex', '-1');
					results.focus({ preventScroll: true });
					results.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			})
			.catch(function (error) {
				if (error && error.name === 'AbortError') {
					return;
				}

				window.location.assign(requestUrl.toString());
			})
			.finally(function () {
				setLoadingState(form, results, false);
			});
	};

	form.addEventListener('submit', function (event) {
		event.preventDefault();
		updatePageField(form, 1);
		requestResults({ focusResults: true });
	});

	form.addEventListener('gmr-async-request', function () {
		updatePageField(form, 1);
		requestResults();
	});

	const searchInputs = form.querySelectorAll('input[name="s"]');

	searchInputs.forEach(function (input) {
		input.addEventListener(
			'input',
			debounce(function () {
				updatePageField(form, 1);
				requestResults();
			}, 300)
		);
	});

	form.addEventListener('change', function (event) {
		const field = event.target;

		if (!field || field.nodeType !== Node.ELEMENT_NODE) {
			return;
		}

		if (field.matches('select,input[type="checkbox"],input[type="radio"]')) {
			updatePageField(form, 1);
			requestResults();
		}
	});

	results.addEventListener('click', function (event) {
		const trigger = event.target.closest('[data-gmr-page-link]');

		if (!trigger || trigger.closest('[aria-disabled="true"]')) {
			return;
		}

		event.preventDefault();

		updatePageField(form, Number.parseInt(trigger.dataset.gmrPageLink || '1', 10) || 1);
		/*
		 * Pagination is different from typing into a search/filter field. After a
		 * visitor clicks a page number at the bottom of a result list, the newly
		 * rendered page begins above their current viewport, so opted-in listings
		 * should move focus and scroll back to the refreshed result container.
		 */
		requestResults(
			shouldScrollPageChangesToResults
				? { focusResults: true }
				: { preserveScroll: true }
		);
	});
}

export function initAsyncContent() {
	const forms = document.querySelectorAll('[data-gmr-async-form]');

	if (!forms.length) {
		return;
	}

	Array.from(forms).forEach(initAsyncForm);
}
